import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import { DataTable, type Column } from '@/components/ui/DataTable';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { SortableHandle, SortableItem, SortableList } from '@/components/ui/SortableList';
import { useSortableMoves } from '@/components/ui/sortableItemContext';
import { formatDate } from '@/lib/organization';

import { ReasonedButton } from './ReasonedButton';
import {
  addLabel,
  emptyWords,
  itemStatus,
  itemTitle,
  resourceTypeLabel,
  type ArcItem,
  type ArcSection,
} from './arcTypes';

interface SectionItemsTableProps {
  section: ArcSection;
  /** The section's test ID prefix, `arc-builder-section-<id>`. */
  prefix: string;
  readOnly: boolean;
  /** Why no more content can be added here, or null (B16). */
  addReason: string | null;
  onRemove: (item: ArcItem) => void;
  /** Opens Move to section… for an item. */
  onMove: (item: ArcItem) => void;
  /** The section's content in a new order, after a drag or a Move button. */
  onReorder: (ids: string[]) => void;
  /** Opens the Add Content picker. */
  onAdd: () => void;
}

/**
 * The content placed in one section, in the order the app shows it.
 *
 * An item whose resource has gone (deleted, unpublished, or another
 * organization's) stays listed as Unavailable, so the Admin can see what
 * happened to it, and can only be removed.
 *
 * Laid out as the wireframe draws it (Part 2, page 4). Each row has a drag
 * handle; its menu offers Move up and Move down for anybody who cannot drag,
 * and Move to section… for another section. The Add button under the table
 * opens the Add Content picker (Day 27).
 */
export function SectionItemsTable({
  section,
  prefix,
  readOnly,
  addReason,
  onRemove,
  onMove,
  onReorder,
  onAdd,
}: SectionItemsTableProps) {
  const items = [...section.items].sort((a, b) => a.position - b.position);

  const columns: Column<ArcItem>[] = [
    {
      key: 'order',
      header: 'Order',
      headerHidden: true,
      render: () => <SortableHandle />,
    },
    {
      key: 'content',
      header: 'Content',
      // Titles and details may be long and unbroken; they wrap in the cell (B28).
      render: (item) => (
        <Wraps>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {item.available ? itemTitle(item) : 'Unavailable'}
          </Box>
        </Wraps>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (item) => (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {resourceTypeLabel(item.resource_type)}
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <Typography
          variant="body2"
          data-testid="arc-builder-item-status-chip"
          sx={{ fontWeight: 600 }}
        >
          {itemStatus(item).label}
        </Typography>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (item) => (
        <Wraps>
          {item.available && item.resource
            ? (item.resource.details ?? '—')
            : 'No longer available'}
        </Wraps>
      ),
    },
    {
      key: 'updated',
      header: 'Last Updated',
      render: (item) =>
        item.available && item.resource ? formatDate(item.resource.updated_at) : '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (item) => (
        <ItemActions
          item={item}
          readOnly={readOnly}
          onMove={onMove}
          onRemove={onRemove}
        />
      ),
    },
  ];

  return (
    <Box>
      <SortableList
        ids={items.map((item) => item.id)}
        nameOf={(id) => {
          const item = items.find((entry) => entry.id === id);
          return item ? itemTitle(item) : 'Content';
        }}
        onReorder={onReorder}
        disabled={readOnly}
      >
        <DataTable
          columns={columns}
          rows={items}
          getRowKey={(item) => item.id}
          caption={`${section.name} content`}
          emptyMessage={emptyWords(section)}
          wrapRow={(item, render) => (
            <SortableItem id={item.id} testId="arc-builder-item" moveButtons={false}>
              {({ ref, style }) => render({ ref, style })}
            </SortableItem>
          )}
          testId={`${prefix}-table`}
        />
      </SortableList>
      {readOnly ? null : (
        <Box sx={{ mt: 1.5 }}>
          <ReasonedButton
            variant="secondary"
            reason={addReason}
            onClick={onAdd}
            testId={`${prefix}-add-button`}
          >
            + {addLabel(section)}
          </ReasonedButton>
        </Box>
      )}
    </Box>
  );
}

/** One row's menu: moving it within the section, to another section, or out of it. */
function ItemActions({
  item,
  readOnly,
  onMove,
  onRemove,
}: {
  item: ArcItem;
  readOnly: boolean;
  onMove: (item: ArcItem) => void;
  onRemove: (item: ArcItem) => void;
}) {
  const moves = useSortableMoves();
  return (
    <RowActionsMenu
      rowLabel={item.available ? itemTitle(item) : 'unavailable content'}
      testId="arc-builder-item-actions"
      trigger="boxed-dots"
      actions={[
        {
          id: 'move-up',
          label: 'Move up',
          onSelect: moves.moveUp,
          disabled: !moves.canMoveUp,
        },
        {
          id: 'move-down',
          label: 'Move down',
          onSelect: moves.moveDown,
          disabled: !moves.canMoveDown,
        },
        ...(item.available
          ? [
              {
                id: 'move',
                label: 'Move to section…',
                onSelect: () => onMove(item),
                disabled: readOnly,
              },
            ]
          : []),
        {
          id: 'remove',
          label: 'Remove from section',
          onSelect: () => onRemove(item),
          disabled: readOnly,
        },
      ]}
    />
  );
}

/** Text that may break anywhere, so a long unbroken name stays inside its cell. */
function Wraps({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Box>
  );
}
